'use client';

import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import BasicCard from '@/components/common/basic-card';
import Dropdown from '@/components/common/dropdown';
import PaginationComponent from '@/components/common/pagination';
import { useAuthUser } from '@/lib/auth/common/useSession';
import axiosHandler from '@/utils/axios-handler';
import { getDuration } from '@/lib/utils/duration';
import { academyApiUrl } from '@/lib/constants/api';

function useScreenSize() {
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize('mobile');
      } else if (width < 1024) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
}

/** Item can be a course, standalone content, or learning path */
interface Course {
  uid: string;
  thumbnail: {
    url: string;
  };
  url: string;
  title: string;
  publish_details: {
    time: string;
  };
  duration?: string;
  content_type?: string;
  /** For course_details: distinguishes standalone (/content) vs course (/courses) */
  is_standalone?: boolean;
  /** For learning_pathways: used for duration and type label */
  _content_type_uid?: string;
  learning_pathway_details?: {
    duration_in_minutes?: number;
  };
}

interface UserCourseProgress {
  entryUid: string;
  progress?: {
    hasStarted?: boolean;
    percentage: number;
    isCompleted: boolean;
    completedAt?: string | null;
  };
}

interface CourseCarouselClientProps {
  courses: Course[];
  title?: string;
  /** 'homePage' = card-hover-zoom style (like CardsSection), 'default' = arrow/overlay, 'none' = no hover */
  hoverVariant?: 'homePage' | 'default' | 'none';
}

type SortOption = 'Most relevant' | 'Most recent' | 'Not Watched';

export default function CourseCarouselClient({ courses, title, hoverVariant = 'default' }: CourseCarouselClientProps) {
  const [sortOption, setSortOption] = useState<SortOption>('Most relevant');
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed for pagination
  const screenSize = useScreenSize();

  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator) —
  // a useState seeded once from isUserLoggedIn() only ever reflected auth state as of
  // mount, so this carousel's progress data never picked up a login/logout that didn't
  // also remount it.
  const { isLoggedIn } = useAuthUser();

  const { data: userCourses } = useQuery({
    queryKey: ['user-courses-progress'],
    queryFn: async () => {
      // No isUserLoggedIn() re-check here — `enabled: isLoggedIn` below already
      // guarantees this never runs while logged out.
      const response = await axiosHandler({
        method: 'GET',
        url: academyApiUrl('/v1/users/courses'),
        withCredentials: true,
      });

      if ('data' in response && response.status === 200) {
        const data = response.data?.courses || [];
        return Array.isArray(data) ? data : [];
      }
      return [];
    },
    enabled: isLoggedIn,
    staleTime: 0,
  });

  const coursesWithProgress = useMemo(() => {
    // isLoggedIn, not just data truthiness: `enabled` turning off on logout doesn't
    // clear this query's already-fetched data, so without this check the previous
    // session's "started" badges would keep showing after logout.
    if (!isLoggedIn || !userCourses || userCourses.length === 0) {
      return courses.map((course) => ({ ...course, hasStarted: null }));
    }

    const userProgressMap = new Map(userCourses.map((uc: UserCourseProgress) => [uc.entryUid, uc]));

    return courses.map((course) => {
      const userProgress = userProgressMap.get(course.uid);
      const hasStarted = userProgress?.progress?.hasStarted ?? null;

      return {
        ...course,
        hasStarted: hasStarted,
      };
    });
  }, [courses, userCourses, isLoggedIn]);

  const sortedCourses = useMemo(() => {
    let coursesCopy = [...coursesWithProgress];

    switch (sortOption) {
      case 'Most recent':
        return coursesCopy.sort((a, b) => {
          const dateA = new Date(a.publish_details.time || 0);
          const dateB = new Date(b.publish_details.time || 0);
          return dateB.getTime() - dateA.getTime();
        });

      case 'Not Watched':
        return coursesCopy.filter((course) => course.hasStarted !== true);

      case 'Most relevant':
      default:
        return coursesCopy;
    }
  }, [coursesWithProgress, sortOption]);

  const cardsPerPage = useMemo(() => {
    switch (screenSize) {
      case 'mobile':
        return 2;
      case 'tablet':
        return 4;
      case 'desktop':
      default:
        return 8;
    }
  }, [screenSize]);

  const totalPages = Math.ceil(sortedCourses.length / cardsPerPage);

  const currentPageCourses = useMemo(() => {
    const startIndex = currentPage * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    return sortedCourses.slice(startIndex, endIndex);
  }, [sortedCourses, currentPage, cardsPerPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [sortOption]);

  const shouldShowPagination = courses.length > cardsPerPage;

  /** Resolve URL, duration, and content type for BasicCard based on item type (learning path, course, or standalone) */
  const getCardProps = (item: Course) => {
    const isLearningPath = item._content_type_uid === 'learning_pathways';
    if (isLearningPath) {
      const url = item.url?.startsWith('/learning-paths') ? item.url : `/learning-paths${item.url}`;
      const minutes = item.learning_pathway_details?.duration_in_minutes;
      const duration = minutes != null ? getDuration(minutes * 60) : '';
      return { url, duration, contentType: 'Learning Path' };
    }
    // Course or standalone: server sends full url (/courses/... or /content/...)
    return {
      url: item.url ?? '',
      duration: item.duration ?? '',
      contentType: item.content_type ?? (item.is_standalone ? 'Content' : 'Course'),
    };
  };

  return (
    <>
      {title && (
        <div className="desktop:flex-row mb-[32px] flex items-center justify-between">
          <h3 className="desktop:cs-h3 mobile:cs-h6 fit-content">{title}</h3>
          <Dropdown
            options={[
              { value: 'Most relevant', label: 'Most Relevant' },
              { value: 'Most recent', label: 'Most Recent' },
              { value: 'Not Watched', label: 'Not Watched' },
            ]}
            eyebrowText="Sort:"
            defaultValue="Most relevant"
            className="!w-[200px] show-dropdown-bg cursor-pointer"
            onChange={(value) => setSortOption(value as SortOption)}
          />
        </div>
      )}

      <div className="grid auto-rows-max grid-cols-1 gap-8 md:grid-cols-2 md:gap-8 lg:grid-cols-4">
        {currentPageCourses.map((course: Course) => {
          const { url, duration, contentType } = getCardProps(course);
          return (
            <BasicCard
              key={course.uid}
              url={url}
              thumbnail={course?.thumbnail}
              title={course?.title}
              duration={duration}
              contentType={contentType}
              hoverVariant={hoverVariant}
            />
          );
        })}
      </div>

      {shouldShowPagination && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}
    </>
  );
}
